// The release of the mobile app is the BuddyPro lifecycle, 1:1 (F137 Q5+Q6).
//
// 🔴 Pavel 2026-08-11, verbatim: "Musíme to udělat stejně, jako se to dělá
// v BuddyPro. Úplně stejně."
//
// Until F137 this step was OUR invention: a four-phase checklist (Apple account,
// Sestavení aplikace, Kontrola App Store, Kontrola Google Play) that nobody on
// the platform side has ever had. The audit that produced this file read the
// platform's own source and found a single SERVER-CONTROLLED state machine over
// the whole app, no per-store tracking at all: Google Play is one boolean of the
// wizard (`stores.google`) plus a `playStoreUrl` after the release, and the
// phase "Sestavení aplikace" does not exist there - `in_publishing` covers it,
// because their team builds AND submits.
//
// SOURCE (copied by hand, read 2026-08-11 from `origin/main` of the platform
// repo `buddy-fm/buddy`):
//   turborepo/apps/api-internal/src/constants/mobile-app-onboarding.ts
// The two constants below are a byte-for-byte transcription of theirs.
// `tests/app-spousteni-f136.test.ts` pins them as a snapshot: if this file and
// the platform ever drift, the drift is a decision somebody makes on purpose,
// not something that happens quietly.
//
// What we deliberately did NOT copy: their wizard steps, asset slots, price
// tiers and text limits. Those already live in `shared/app-wizard.ts`, which
// the F135 audit measured as matching the platform step for step and limit for
// limit. A second copy here would be the F112 mistake (one catalog, two files,
// one of them stale).

/** Their `ONBOARDING_STATUSES`, in their order. */
export const APP_RELEASE_STATUSES = [
  "draft",
  "submitted_for_publishing",
  "in_publishing",
  "live",
  "rejected",
  "archived"
] as const;

export type AppReleaseStatus = (typeof APP_RELEASE_STATUSES)[number];

/** Their `STATUS_TRANSITIONS`. "Anything else -> 400", in their words. */
export const APP_RELEASE_TRANSITIONS: Record<AppReleaseStatus, readonly AppReleaseStatus[]> = {
  draft: ["submitted_for_publishing"],
  submitted_for_publishing: ["in_publishing", "draft"],
  in_publishing: ["live", "rejected"],
  live: ["archived"],
  rejected: ["draft"],
  archived: []
};

/** Their `OWNER_EDITABLE_STATUSES`: when the owner may still change the config. */
export const APP_RELEASE_EDITABLE_STATUSES: readonly AppReleaseStatus[] = ["draft", "rejected"];

export function isAppReleaseStatus(value: unknown): value is AppReleaseStatus {
  return typeof value === "string" && (APP_RELEASE_STATUSES as readonly string[]).includes(value);
}

/** Exactly the platform's rule, and the ONE answer to "may this move happen". */
export function appReleaseTransitionAllowed(from: AppReleaseStatus, to: AppReleaseStatus): boolean {
  return APP_RELEASE_TRANSITIONS[from].includes(to);
}

export function appReleaseNextStatuses(from: AppReleaseStatus): AppReleaseStatus[] {
  return [...APP_RELEASE_TRANSITIONS[from]];
}

/**
 * The stored shape, inside the `app_state` client resource next to
 * `apple_account` (F135's precedent: the step's facts live where the step's
 * other facts live, and no new table is opened for one string).
 */
export interface AppReleaseValue {
  status: AppReleaseStatus;
  changed_at?: string;
  changed_by?: string;
}

/**
 * Which stores this client actually ships to. Read from the wizard's own
 * `basics.stores`, with the platform's `DEFAULT_STORE_SELECTION` when the step
 * has not been filled in - never "both, because that is the usual case".
 */
export interface AppStoreSelection {
  apple: boolean;
  google: boolean;
}

export const APP_RELEASE_DEFAULT_STORES: AppStoreSelection = { apple: true, google: true };

export function appStoreSelectionFrom(basics: unknown): AppStoreSelection {
  const stores = isRecord(basics) && isRecord(basics.stores) ? basics.stores : null;
  if (!stores) return APP_RELEASE_DEFAULT_STORES;
  const apple = stores.apple === true;
  const google = stores.google === true;
  // The platform's own invariant: never neither.
  if (!apple && !google) return APP_RELEASE_DEFAULT_STORES;
  return { apple, google };
}

/**
 * 🔴 The store links a LIVE app must carry, decided by the client's own store
 * selection - not "both, always".
 *
 * This is the F134 lesson applied BEFORE it costs a round rather than after.
 * S22 and the `app_live` milestone both demanded an iOS *and* an Android URL,
 * so an Apple-only client (a selection the wizard offers and gates ten steps
 * on) could never satisfy the transition that ends their journey: a guard that
 * cannot pass, with no hand escape, which is exactly the archaeology F134 had
 * to dig out of S17/S20. A client who ships to both stores is unchanged, and
 * `appStoreSelectionFrom` defaults to both, so nothing loosens by accident.
 */
export function requiredStoreUrlKeys(stores: AppStoreSelection): Array<"ios" | "android"> {
  const keys: Array<"ios" | "android"> = [];
  if (stores.apple) keys.push("ios");
  if (stores.google) keys.push("android");
  return keys;
}

export function storeUrlsSatisfy(value: unknown, stores: AppStoreSelection): boolean {
  if (!isRecord(value)) return false;
  return requiredStoreUrlKeys(stores).every((key) => {
    const url = value[key];
    return typeof url === "string" && /^https:\/\/\S/.test(url);
  });
}

/**
 * The status to show, from every fact we hold. Stored value ALWAYS wins: a
 * partner who sent the app back to editing has said something the derivation
 * must not talk over.
 *
 * The derivation exists so nothing locks back and no backfill script is needed
 * (F101's law). A client whose step already closed reads `live`; a client who
 * submitted their wizard before this shipped reads `submitted_for_publishing`,
 * because on the platform the submit IS "odesláno k publikaci".
 */
export function appReleaseStatusFrom(input: {
  stored?: unknown;
  releaseStepDone: boolean;
  wizardSubmitted: boolean;
}): AppReleaseStatus {
  const stored = isRecord(input.stored) ? input.stored.status : undefined;
  if (isAppReleaseStatus(stored)) return stored;
  if (input.releaseStepDone) return "live";
  if (input.wizardSubmitted) return "submitted_for_publishing";
  return "draft";
}

/**
 * The client sending their wizard IS "odesláno k publikaci" (F137 Q6), so the
 * submit moves the lifecycle and no partner has to mirror the click.
 *
 * Returns null when the submit changes nothing about the release: an app
 * already submitted, in publishing, live or archived is past this point, and a
 * re-submit must not drag it backwards.
 *
 * 🔴 `rejected` is accepted as a source even though the platform's catalog only
 * offers `rejected -> draft`. Their flow reaches the same place in two moves
 * (the owner edits, which reopens the config to `draft` - their
 * `status.reopened` - and then submits); our client does both in ONE act,
 * because the wizard has no separate "reopen" button to press. The end state is
 * theirs, and no state outside their list is ever written.
 */
export function appReleaseStatusAfterWizardSubmit(current: AppReleaseStatus): AppReleaseStatus | null {
  return current === "draft" || current === "rejected" ? "submitted_for_publishing" : null;
}

/**
 * May the client still edit the wizard? Their `OWNER_EDITABLE_STATUSES`. A
 * partner sending the app back to editing has to give the form back, or the
 * client reads "upravte označené kroky" over a wizard that refuses every key.
 */
export function appReleaseOwnerEditable(status: AppReleaseStatus): boolean {
  return APP_RELEASE_EDITABLE_STATUSES.includes(status);
}

/**
 * What the CLIENT is allowed to meet. Pavel's rule from F137 Q5: where the
 * result is seen by the CUSTOMER the customer clicks, where WE see it we click
 * - and a client never sees a store review at all, so every status they read is
 * a calm description with no control on it. `archived` is deliberately folded
 * into `live`'s neighbourhood rather than announced: an archived app is our
 * bookkeeping, not news to break on the client's dashboard.
 */
export type AppReleaseTone = "waiting" | "working" | "live" | "returned" | "closed";

export const APP_RELEASE_CLIENT_TONE: Record<AppReleaseStatus, AppReleaseTone> = {
  draft: "waiting",
  submitted_for_publishing: "working",
  in_publishing: "working",
  live: "live",
  rejected: "returned",
  archived: "closed"
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
